"use client";

import { useState, useEffect } from "react";
import {
  ShoppingCart,
  DollarSign,
  Clock,
  Plus,
  User,
  Building2,
  Trash2,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useParams } from "next/navigation";
import { CartSidebarProps } from "@/types/booking-new.types";
import {
  PRICE_TYPE_IDS,
  PRICE_TYPE_NAMES,
  getPriceMode,
} from "@/lib/constants/price-types";
import { STATUS_IDS } from "@/lib/constants/statuses";
import {
  formatOrgNumber,
  orgNumberExample,
  venueCompanyCountry,
} from "@/lib/utils/company-info";
import { PricingService } from "@/lib/pricing/pricing-service-unified";
import { calculateBookingTotals } from "@/lib/pricing/booking-calculations";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { CUSTOMER_TYPE_IDS } from "@/lib/constants/customer-types";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import { useVenueDetails } from "@/hooks/useVenueDetails";
import type { BookingData, BookingAddOn } from "@/lib/pricing/types";
import { INVOICE_REFERENCE_MAX_LENGTH } from "@/lib/constants/accounting";

// Phone validation helper - only validates, doesn't modify
function validatePhone(phone: string): {
  isValid: boolean;
  error?: string;
} {
  // Handle empty input
  if (!phone || typeof phone !== "string") {
    return {
      isValid: true, // Empty is valid (optional field)
      error: undefined,
    };
  }

  // Clean the input - only for validation, not modification
  const cleaned = phone.trim();

  // Handle empty input after trimming
  if (!cleaned) {
    return {
      isValid: true, // Empty is valid (optional field)
      error: undefined,
    };
  }

  // Extract digits only for pattern checking
  const digitsOnly = cleaned.replace(/[^\d]/g, "");

  // Check for obviously invalid patterns
  if (
    digitsOnly.match(/^0+$/) || // All zeros
    digitsOnly.match(/^1+$/) || // All ones
    digitsOnly.match(/^2+$/) || // All twos
    digitsOnly.match(/^3+$/) || // All threes
    digitsOnly.match(/^4+$/) || // All fours
    digitsOnly.match(/^5+$/) || // All fives
    digitsOnly.match(/^6+$/) || // All sixes
    digitsOnly.match(/^7+$/) || // All sevens
    digitsOnly.match(/^8+$/) || // All eights
    digitsOnly.match(/^9+$/) || // All nines
    digitsOnly === "123456789" || // Sequential test number
    digitsOnly === "1234567890" // Another test pattern
  ) {
    return {
      isValid: false,
      error: "Please enter a valid phone number",
    };
  }

  // Check length
  if (digitsOnly.length < 7) {
    return {
      isValid: false,
      error: "Phone number is too short",
    };
  }

  if (digitsOnly.length > 15) {
    return {
      isValid: false,
      error: "Phone number is too long",
    };
  }

  // Basic format check - allow various formats
  // Allow: +, spaces, dashes, parentheses, and digits
  const basicFormatRegex = /^[\d\s\-\(\)\+]+$/;
  if (!basicFormatRegex.test(cleaned)) {
    return {
      isValid: false,
      error: "Phone number contains invalid characters",
    };
  }

  return {
    isValid: true,
  };
}

// Format phone to E.164 format
function formatPhoneToE164(phone: string): string {
  if (!phone || typeof phone !== "string") {
    return "";
  }

  let formattedPhone = phone.trim();

  // If empty, return empty
  if (!formattedPhone) {
    return "";
  }

  // If phone already starts with +, use as is (assume it's already in E.164 format)
  if (formattedPhone.startsWith("+")) {
    // Clean up any non-digit characters except the leading +
    return "+" + formattedPhone.slice(1).replace(/\D/g, "");
  }

  // For numbers without country code, add + and clean up
  // Remove all non-digit characters
  const digitsOnly = formattedPhone.replace(/\D/g, "");

  // Add + prefix for E.164 format
  return "+" + digitsOnly;
}

// Format phone number for display
function formatPhoneForDisplay(phone: string): string {
  if (!phone || typeof phone !== "string") {
    return "";
  }

  const cleaned = phone.trim();

  // If empty, return empty
  if (!cleaned) {
    return "";
  }

  // Extract digits only
  const digitsOnly = cleaned.replace(/\D/g, "");

  // If empty after removing non-digits, return original
  if (!digitsOnly) {
    return cleaned;
  }

  // Always format with + prefix for international format
  // Check for specific country patterns
  if (digitsOnly.startsWith("1") && digitsOnly.length === 11) {
    // US/Canada format: +1 (XXX) XXX-XXXX
    return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  } else if (digitsOnly.startsWith("45") && digitsOnly.length === 10) {
    // Denmark format: +45 XX XX XX XX
    return `+${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 6)} ${digitsOnly.slice(6, 8)} ${digitsOnly.slice(8)}`;
  } else if (digitsOnly.startsWith("46") && digitsOnly.length === 11) {
    // Sweden format: +46 XX XXX XX XX
    return `+${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 9)} ${digitsOnly.slice(9)}`;
  } else if (digitsOnly.startsWith("47") && digitsOnly.length === 10) {
    // Norway format: +47 XXX XX XXX
    return `+${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 5)} ${digitsOnly.slice(5, 7)} ${digitsOnly.slice(7)}`;
  } else if (digitsOnly.length === 10) {
    // Could be US without country code: +1 (XXX) XXX-XXXX
    // Or another country - format generically
    return `+${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
  } else if (digitsOnly.length === 8) {
    // Danish/Nordic format without country code: assume Denmark +45
    return `+45 ${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 4)} ${digitsOnly.slice(4, 6)} ${digitsOnly.slice(6)}`;
  } else if (digitsOnly.length >= 11) {
    // Generic international format with country code
    let formatted = "+";
    let i = 0;

    // Determine country code length (1-3 digits)
    let countryCodeLength = 1;
    if (digitsOnly.length >= 12) {
      countryCodeLength = 2;
    }
    if (digitsOnly.length >= 13) {
      countryCodeLength = 3;
    }

    formatted += digitsOnly.slice(0, countryCodeLength) + " ";
    i = countryCodeLength;

    // Format the rest in groups of 3-4
    while (i < digitsOnly.length) {
      if (i + 4 <= digitsOnly.length) {
        formatted += digitsOnly.slice(i, i + 4) + " ";
        i += 4;
      } else {
        formatted += digitsOnly.slice(i);
        break;
      }
    }

    return formatted.trim();
  } else {
    // For other lengths, just add + and format in groups
    let formatted = "+";
    let i = 0;

    while (i < digitsOnly.length) {
      if (i + 3 <= digitsOnly.length) {
        formatted += digitsOnly.slice(i, i + 3) + " ";
        i += 3;
      } else {
        formatted += digitsOnly.slice(i);
        break;
      }
    }

    return formatted.trim();
  }
}

// Utility function to format price with conditional decimals
function formatPrice(value: number): string {
  const rounded = Math.round(value * 100) / 100; // Round to 2 decimal places
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2);
}

export function CartSidebar({
  open,
  onOpenChange,
  bookingCart,
  onGroupSizeChange,
  onRemoveShow,
  onUpdateShowTime,
  onUpdateShowStudio,
  onQuantityChange,
  onRemoveAddon,
  onSaveBooking,
  venueCurrency,
  venueDefaultLanguage,
  bookingId,
  studios,
  onUpdateAddonPrice,
  isSavingBooking = false,
  activePriceModifier,
  bookingPrice,
  onBookingPriceChange,
  onPriceManuallyChanged,
  onPriceModeChange,
  onDateChange,
  onLanguageChange,
  onKitChange,
  onRoadShowBasePriceChange,
  onRoadShowPerPersonPriceChange,
  availableLanguages,
  existingBookingPricing,
}: CartSidebarProps) {
  const params = useParams();
  const venueId = params?.venueId as string;

  // Road-show equipment kits available on the selected day.
  const [availableKits, setAvailableKits] = useState<
    Array<{ id: number; name: string }>
  >([]);

  // Offer ONLY the venue's languages. Source them from the venue details hook
  // (same as the rest of the booking flow) and merge any languages passed in.
  // Never fall back to the full language set.
  const { languages: venueLanguages } = useVenueDetails(venueId || null);
  // The cart has no company country field, so the org number follows the
  // venue's country (only Swedish venues write it with a dash). Danish venues
  // label it "CVR number".
  const orgNumberCountry = venueCompanyCountry(
    venueCurrency,
    venueDefaultLanguage,
  );
  const isDanishVenue = orgNumberCountry === "DK";
  const languageChoices = (() => {
    const allowed = new Set(
      [...(venueLanguages ?? []), ...(availableLanguages ?? [])]
        .map((l) => (l || "").split("-")[0].toLowerCase())
        .filter(Boolean),
    );
    const filtered = AVAILABLE_LANGUAGES.filter((l) => allowed.has(l.code));
    // Last-resort fallback is English only — never the entire language list.
    return filtered.length > 0
      ? filtered
      : AVAILABLE_LANGUAGES.filter((l) => l.code === "en");
  })();

  // Local states for the sidebar
  const [priceMode, setPriceMode] = useState<"per_person" | "fixed">(
    "per_person",
  );
  const [bookingDate, setBookingDate] = useState<Date | null>(null);
  const [addonPrices, setAddonPrices] = useState<Record<number, number>>({});
  const [bookingStatus, setBookingStatus] = useState<number | null>(null);
  const [statusInitialized, setStatusInitialized] = useState(false);

  // Helper function to update price mode and notify parent
  const handlePriceModeChange = (mode: "per_person" | "fixed") => {
    setPriceMode(mode);
    if (onPriceModeChange) {
      onPriceModeChange(mode);
    }
  };

  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const [companyInfo, setCompanyInfo] = useState({
    companyName: "",
    orgNumber: "",
    address: "",
    invoiceReference: "",
  });

  // Partner picker — populated lazily when the cart opens for a corporate
  // booking. Selecting a partner snapshots its id (sent to the API) and
  // auto-fills the company fields. Staff can still edit the fields after.
  type PartnerOption = {
    id: number;
    company_name: string;
    organization_number: string | null;
    address_line_1: string;
    address_line_2: string | null;
    city: string;
    postcode: string;
    country: string;
    invoice_reference: string | null;
    commission_percentage: number;
  };
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
    null,
  );
  // Free-text end-customer / project label, captured only when a partner
  // is selected. Surfaces in the invoice text (Fortnox Remarks). Optional.
  const [endCustomerName, setEndCustomerName] = useState<string>("");

  useEffect(() => {
    // Only fetch when the cart opens for a corporate booking (the only
    // surface that uses partners). Cached for the lifetime of the cart.
    if (
      !open ||
      bookingCart.bookingType !== "corporate" ||
      !venueId ||
      partners.length > 0
    ) {
      return;
    }
    let cancelled = false;
    fetch(`/api/staff/venues/${venueId}/partners`)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json?.success && Array.isArray(json.data)) {
          setPartners(json.data as PartnerOption[]);
        }
      })
      .catch(() => {
        // Non-fatal: corporate flow still works without the picker.
      });
    return () => {
      cancelled = true;
    };
  }, [open, bookingCart.bookingType, venueId, partners.length]);

  const handlePartnerChange = (value: string) => {
    if (value === "__none__") {
      setSelectedPartnerId(null);
      setEndCustomerName("");
      return;
    }
    const idNum = parseInt(value, 10);
    if (Number.isNaN(idNum)) return;
    const p = partners.find((x) => x.id === idNum);
    if (!p) return;
    setSelectedPartnerId(p.id);
    // Auto-fill the corporate form. Staff retains the ability to edit.
    const addressParts = [
      p.address_line_1,
      p.address_line_2,
      [p.postcode, p.city].filter(Boolean).join(" "),
      p.country,
    ].filter(Boolean);
    setCompanyInfo((prev) => ({
      ...prev,
      companyName: p.company_name,
      orgNumber: p.organization_number ?? "",
      address: addressParts.join(", "),
      invoiceReference: p.invoice_reference ?? "",
    }));
  };

  // Validation states
  const [phoneError, setPhoneError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [displayPhone, setDisplayPhone] = useState<string>("");
  const [timeConflicts, setTimeConflicts] = useState<Record<number, string>>({});
  const [dateConflicts, setDateConflicts] = useState<string[]>([]);

  // Function to check for date conflicts
  const checkDateConflict = async (newDate: Date) => {
    // Clear previous date conflicts
    setDateConflicts([]);

    // Check conflicts for all shows in cart
    const hardConflicts: string[] = [];

    for (let i = 0; i < bookingCart.shows.length; i++) {
      const show = bookingCart.shows[i];
      if (!show) continue;

      // Format date for API
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, "0");
      const day = String(newDate.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      // Calculate actual duration
      let actualDuration = show.show.duration_minutes || 60;
      if (show.customEndTime && show.customEndTime !== "") {
        const [startHour, startMin] = show.time.split(":").map(Number);
        const [endHour, endMin] = show.customEndTime.split(":").map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        if (endMinutes > startMinutes) {
          actualDuration = endMinutes - startMinutes;
        }
      }

      // Check studio availability
      const studioParams = new URLSearchParams({
        show_id: show.show.id.toString(),
        date: formattedDate,
        time_slot: show.time,
      });

      if (bookingId) {
        studioParams.append("exclude_booking_id", bookingId.toString());
      }

      // Pass custom duration if different from default
      if (actualDuration !== show.show.duration_minutes) {
        studioParams.append("custom_duration", actualDuration.toString());
      }

      try {
        // Check studio availability
        const studioResponse = await fetch(
          `/api/bookings/studio-availability?${studioParams}`,
        );
        const studioData = await studioResponse.json();

        if (studioData.success && studioData.data) {
          // Check if the selected studio is available
          const selectedStudioId = show.studio.id;
          const isStudioAvailable = studioData.data.available_studio_ids.includes(selectedStudioId);

          if (!isStudioAvailable) {
            hardConflicts.push(`${show.show.name} at ${show.time} - Studio ${show.studio.name} is already booked`);
          }
        }

        // The 15-minute rule between shows: not a warning, saving is refused.
        // Road shows run off-site, so it does not apply to them.
        const gapResponse = bookingCart.bookingType === "roadshow" ? null : await fetch("/api/bookings/check-gap", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            showDate: formattedDate,
            startTime: show.time,
            duration: actualDuration,
            venueId: parseInt(venueId),
            studioId: show.studio.id,
            isStaffRequest: true,
            excludeBookingId: bookingId || undefined,
          }),
        });
        if (gapResponse?.ok) {
          const gapResult = await gapResponse.json();
          if (gapResult.hasConflict) {
            const conflict = gapResult.conflictingBooking;
            hardConflicts.push(
              `${show.show.name} at ${show.time} - less than 15 minutes from "${conflict.showName}" (${conflict.startTime} - ${conflict.endTime}). Pick another time.`,
            );
          }
        }
      } catch (error) {
        console.error("Error checking availability:", error);
      }
    }

    // Show conflicts and warnings
    const allConflicts = hardConflicts.map(msg => `🚫 ${msg}`);

    setDateConflicts(allConflicts);
  };

  // Function to check for time conflicts
  // `endTime` is the end just set ("" for the show's own length). The cart
  // state only updates after this runs, so the edit is passed in rather than
  // read from it.
  const checkTimeConflict = async (
    showIndex: number,
    time: string,
    endTime?: string,
  ) => {
    const show = bookingCart.shows[showIndex];
    if (!show) return;
    const customEndTime = endTime !== undefined ? endTime : show.customEndTime;

    // Calculate actual duration based on custom end time if set
    let actualDuration = show.show.duration_minutes || 60;
    if (customEndTime && customEndTime !== "") {
      const [startHour, startMin] = time.split(":").map(Number);
      const [endHour, endMin] = customEndTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      // Only use custom duration if end time is after start time
      if (endMinutes > startMinutes) {
        actualDuration = endMinutes - startMinutes;
      } else {
        // If end time is not valid, use default duration
        actualDuration = show.show.duration_minutes || 60;
      }
    }

    try {
      // First check studio availability (hard conflict - double booking)
      const studioParams = new URLSearchParams({
        show_id: show.show.id.toString(),
        date: show.date,
        time_slot: time,
      });

      // Pass the actual duration if it's different from the default
      if (actualDuration !== show.show.duration_minutes) {
        studioParams.append("custom_duration", actualDuration.toString());
      }

      if (bookingId) {
        studioParams.append("exclude_booking_id", bookingId.toString());
      }

      const studioResponse = await fetch(`/api/bookings/studio-availability?${studioParams}`);

      if (studioResponse.ok) {
        const studioResult = await studioResponse.json();

        if (studioResult.success && studioResult.data) {
          const { available_studio_ids, occupied_studio_ids } = studioResult.data;

          // Check if the current studio is occupied
          if (show.studio?.id && occupied_studio_ids.includes(show.studio.id)) {
            // Studio is double-booked - this is a hard conflict
            setTimeConflicts(prev => ({
              ...prev,
              [showIndex]: `🚫 Studio ${show.studio.name} is already booked during this time slot`
            }));
            return; // Don't check gap if there's a hard conflict
          }
        }
      }

      // If no studio conflict, check for 15-minute gap requirement. Road shows
      // run off-site, so it does not apply to them.
      if (bookingCart.bookingType === "roadshow") return;
      const requestPayload = {
        showDate: show.date,
        startTime: time,
        duration: actualDuration,
        venueId: parseInt(venueId),
        studioId: show.studio?.id,
        isStaffRequest: true,
        excludeBookingId: bookingId || undefined,
      };

      const gapResponse = await fetch("/api/bookings/check-gap", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      });

      if (gapResponse.ok) {
        const gapResult = await gapResponse.json();

        if (gapResult.hasConflict) {
          const conflict = gapResult.conflictingBooking;
          setTimeConflicts(prev => ({
            ...prev,
            [showIndex]: `🚫 Less than 15 minutes from "${conflict.showName}" (${conflict.startTime} - ${conflict.endTime}) in this studio. Pick another time - the booking can't be saved like this.`
          }));
        } else {
          // Clear any existing conflict for this show
          setTimeConflicts(prev => {
            const updated = { ...prev };
            delete updated[showIndex];
            return updated;
          });
        }
      }
    } catch (error) {
      console.error("Error checking time conflict:", error);
    }
  };

  // Phone handling functions
  const handlePhoneChange = (value: string) => {
    setCustomerInfo(prev => ({ ...prev, phone: value }));
    setDisplayPhone(value);

    // Validate in real-time
    if (value) {
      const phoneResult = validatePhone(value);
      if (!phoneResult.isValid) {
        setPhoneError(phoneResult.error || "Invalid phone number");
      } else {
        setPhoneError("");
      }
    } else {
      setPhoneError("");
    }
  };

  const handlePhoneBlur = () => {
    // Format phone number on blur for better display
    const formatted = formatPhoneForDisplay(customerInfo.phone);
    setDisplayPhone(formatted);
  };

  // Initialize display phone when component mounts or phone changes
  useEffect(() => {
    if (customerInfo.phone) {
      setDisplayPhone(formatPhoneForDisplay(customerInfo.phone));
    }
  }, []);

  // Check for time conflicts when cart shows change
  useEffect(() => {
    // Check each show for conflicts
    bookingCart.shows.forEach((show, index) => {
      checkTimeConflict(index, show.time);
    });
  }, [bookingCart.shows, bookingId]);

  // Function to handle save - format phone to E.164
  const handleSaveBooking = (isDraft: boolean) => {
    if (!bookingStatus) {
      return; // Should not happen due to button being disabled
    }

    // Format phone to E.164 before sending
    const formattedCustomerInfo = {
      ...customerInfo,
      phone: customerInfo.phone ? formatPhoneToE164(customerInfo.phone) : ""
    };

    onSaveBooking(
      isDraft,
      formattedCustomerInfo,
      companyInfo,
      priceMode,
      bookingPrice,
      bookingStatus,
      selectedPartnerId,
      selectedPartnerId !== null && endCustomerName.trim()
        ? endCustomerName.trim()
        : null,
    );
  };

  // Booking price is now managed by parent component

  // Reset statusInitialized when sidebar closes or bookingId changes
  useEffect(() => {
    if (!open || !bookingId) {
      setStatusInitialized(false);
    }
  }, [open, bookingId]);

  // Set default status for new bookings
  useEffect(() => {
    if (open && !bookingId && !bookingStatus) {
      setBookingStatus(STATUS_IDS.BOOKING_DRAFT);
      setStatusInitialized(true);
    }
  }, [open, bookingId, bookingStatus]);

  // Fetch customer info when bookingId is present and sidebar opens
  useEffect(() => {
    if (!bookingId || !open) return;

    const fetchCustomerInfo = async () => {
      try {
        const response = await fetch(
          `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const booking = data.data;

            // Set customer info from customer (user_details view)
            if (booking.customer) {
              setCustomerInfo({
                name: booking.customer.name || "",
                email: booking.customer.email || "",
                phone: booking.customer.phone || booking.phone || "", // Try customer.phone first, then booking.phone
                notes: booking.special_request || "", // special_request field contains external notes
              });
            }

            // Set company info if it's a corporate booking
            if (
              booking.customer_type_id === 2 &&
              booking.customer?.company_profile
            ) {
              const companyProfile = booking.customer.company_profile;
              setCompanyInfo({
                companyName: companyProfile.company_name || "",
                orgNumber: companyProfile.organization_number || "",
                address: companyProfile.address_line_1 || "",
                invoiceReference: companyProfile.invoice_reference || "",
              });
            }

            // Pre-fill the partner picker + end-customer name from the
            // saved booking so edit mode shows the existing selection
            // instead of "No partner".
            if (booking.partner_id != null) {
              setSelectedPartnerId(Number(booking.partner_id));
            }
            if (booking.end_customer_name) {
              setEndCustomerName(String(booking.end_customer_name));
            }

            // Set pricing info from booking only if no shows in cart yet
            // Otherwise, the shows useEffect will calculate the correct price
            if (bookingCart.shows.length === 0) {
              // Get price from the booking's assigned pricing or use amount per person
              const bookingPrice =
                booking.amount || booking.assigned_pricing?.price || 500;
              onBookingPriceChange(bookingPrice);
            }

            // Determine price mode based on booking data
            // Check price_type_id to determine the mode
            if (booking.price_type_id) {
              handlePriceModeChange(getPriceMode(booking.price_type_id));
            } else {
              handlePriceModeChange("per_person"); // Default to per_person
            }

            // Set booking status from existing booking
            if (booking.status_id && !statusInitialized) {
              setBookingStatus(booking.status_id);
              setStatusInitialized(true);
            } else if (!booking.status_id) {
              // No status_id in booking response
            } else {
              // Status already initialized, skipping update
            }
          }
        }
      } catch (error) {
        console.error("❌ Error fetching customer info:", error);
      }
    };

    fetchCustomerInfo();
  }, [
    bookingId,
    open,
    venueId,
    bookingCart.shows.length,
    onBookingPriceChange,
  ]);

  // Use the first show's date if available, otherwise use today
  const effectiveBookingDate =
    bookingDate ||
    (bookingCart.shows.length > 0
      ? (() => {
          // Parse date string as local date to avoid timezone issues
          const [year, month, day] = bookingCart.shows[0].date
            .split("-")
            .map(Number);
          return new Date(year, month - 1, day);
        })()
      : new Date());

  // Stable YYYY-MM-DD string for the selected day (used to fetch available kits).
  const effectiveBookingDateStr = `${effectiveBookingDate.getFullYear()}-${String(
    effectiveBookingDate.getMonth() + 1,
  ).padStart(2, "0")}-${String(effectiveBookingDate.getDate()).padStart(2, "0")}`;

  // Load the kits available on the selected day (road shows only).
  useEffect(() => {
    if (!open || bookingCart.bookingType !== "roadshow" || !venueId) return;
    let cancelled = false;
    (async () => {
      try {
        const kitParams = new URLSearchParams({ date: effectiveBookingDateStr });
        // When editing, don't count this booking's own kit as taken.
        if (bookingId) kitParams.set("exclude_booking_id", bookingId);
        // The studio/location being booked — a kit in use can still be shared at
        // this SAME studio, but not across different studios.
        const rsStudioId = (bookingCart.shows[0]?.studio as any)?.id;
        if (rsStudioId != null)
          kitParams.set("studio_id", String(rsStudioId));
        // Scope availability to the selected slot's time window, so a kit busy at
        // another time of day is still offered. Falls back to whole-day when no
        // slot/time is selected yet.
        const rsTime = bookingCart.shows[0]?.time;
        const rsDuration = bookingCart.shows[0]?.duration_minutes;
        if (rsTime) kitParams.set("time", rsTime);
        if (rsDuration) kitParams.set("duration", String(rsDuration));
        const res = await fetch(
          `/api/venues/${venueId}/kits?${kitParams.toString()}`,
        );
        const data = await res.json();
        if (!cancelled && Array.isArray(data.kits)) {
          setAvailableKits(
            data.kits.filter((k: any) => k.status === "active" && k.available),
          );
        }
      } catch {
        if (!cancelled) setAvailableKits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    venueId,
    bookingCart.bookingType,
    effectiveBookingDateStr,
    bookingId,
    (bookingCart.shows[0]?.studio as any)?.id,
    bookingCart.shows[0]?.time,
    bookingCart.shows[0]?.duration_minutes,
  ]);

  // Road-show base fee: use the staff-entered value if present, otherwise fall
  // back to the selected show's base_price so it shows prefilled. Computed at
  // render so it works regardless of when the road-show type was chosen.
  const roadShowShowBasePrice = Number(
    (bookingCart.shows[0]?.show as any)?.base_price,
  );
  const effectiveRoadShowBase =
    bookingCart.roadShowBasePrice ??
    (Number.isFinite(roadShowShowBasePrice) ? roadShowShowBasePrice : null);

  // Per-person rate falls back to the selected show's per-person (slot) price,
  // stored on the cart item as `price`.
  const roadShowSlotPerPerson = Number(bookingCart.shows[0]?.price);
  const effectiveRoadShowPerPerson =
    bookingCart.roadShowPerPersonPrice ??
    (Number.isFinite(roadShowSlotPerPerson) ? roadShowSlotPerPerson : null);

  const handleAddonPriceChange = (addonId: number, price: number) => {
    setAddonPrices((prev) => ({ ...prev, [addonId]: price }));
    if (onUpdateAddonPrice) {
      onUpdateAddonPrice(addonId, price);
    }
  };

  // Convert cart data to BookingData format for pricing engine
  const isPrivateCustomer = bookingCart.bookingType === "private";
  const customerTypeId = isPrivateCustomer
    ? CUSTOMER_TYPE_IDS.PRIVATE
    : CUSTOMER_TYPE_IDS.CORPORATE;
  const priceTypeId =
    priceMode === "per_person"
      ? PRICE_TYPE_IDS.PER_PERSON
      : PRICE_TYPE_IDS.FIXED;

  // A multi-show booking is charged for one show only: the one whose own total
  // (unit price x charged participants, where charged = max(group size, that
  // show's minimum)) is highest. This mirrors BookingPricingEngine, which prices
  // each show with its own minimum and keeps the most expensive result. The
  // winner supplies the VAT rate and the minimum charge shown in the cart.
  const chargedParticipantsFor = (item: (typeof bookingCart.shows)[number]) =>
    priceMode === "per_person"
      ? Math.max(bookingCart.groupSize, item.show.min_charge_participants || 0)
      : 1;
  const showWithMaxPrice = bookingCart.shows.reduce((max, current) => {
    const currentTotal = (current.price || 0) * chargedParticipantsFor(current);
    const maxTotal = max ? (max.price || 0) * chargedParticipantsFor(max) : 0;
    // If totals are equal, prefer the show with higher VAT percentage
    if (currentTotal > maxTotal) {
      return current;
    } else if (currentTotal === maxTotal && (current.vat_percentage || 0) > (max?.vat_percentage || 0)) {
      return current;
    }
    return max;
  }, bookingCart.shows[0]);

  // VAT percentage is required - throw error if missing (0% is a valid rate)
  if (showWithMaxPrice?.vat_percentage == null && bookingCart.shows.length > 0) {
    console.error("Missing VAT percentage in cart shows:", bookingCart.shows);
    throw new Error("VAT percentage is missing from booking data. Please refresh and try again.");
  }

  const showVatPercentage = showWithMaxPrice?.vat_percentage ?? 0; // Use 0 if no shows in cart


  // Calculate base price for shows (excluding VAT)
  // For private customers: entered price includes VAT, so extract the base for calculations
  // For corporate customers: entered price already excludes VAT
  //
  // IMPORTANT: bookingPrice already has price modifiers applied (either from backend pricing engine
  // or from database when editing). Do NOT apply modifiers again here to avoid double application.
  let showUnitPriceExclVat = isPrivateCustomer
    ? PricingService.extractBase(bookingPrice, showVatPercentage)
    : bookingPrice;

  // Convert add-ons to BookingAddOn format
  const bookingAddOns: BookingAddOn[] = bookingCart.addOns.map((item) => {
    const unitPriceInput = addonPrices[item.addOn.id] ?? item.addOn.price;
    // Use the actual quantity from the cart item
    // For new bookings with per-person add-ons, this will be set to groupSize when added
    // For edited bookings, this preserves the custom quantity from the database
    const quantity = item.quantity;
    // VAT percentage is required for add-ons (0% is a valid rate, only null/undefined is missing)
    if (item.addOn.vat_percentage == null) {
      console.error("Missing VAT percentage for add-on:", item.addOn);
      throw new Error(`VAT percentage is missing for add-on: ${item.addOn.name}`);
    }
    const addonVatRate = item.addOn.vat_percentage;

    // item.addOn.price from server is already base price (excl VAT)
    // No need to extract base - it's already the base
    const unitPriceExclVat = unitPriceInput ?? 0;

    const totalExclVat = unitPriceExclVat * quantity;
    const vatAmount = PricingService.calculateVAT(totalExclVat, addonVatRate);

    return {
      id: item.addOn.id,
      add_on_id: item.addOn.id,
      name: item.addOn.name,
      quantity: quantity,
      price: unitPriceExclVat,
      vatPercentage: addonVatRate,
      type: item.addOn.is_per_person ? "per_person" : "fixed",
      total_price_excl_vat: totalExclVat,
      vat_amount: vatAmount, // Always calculate VAT amount
      vat_rate: addonVatRate,
    };
  });

  // Calculate effective participant count (considering minimum charge).
  // Only the charged show's minimum applies; a cheaper show with a larger
  // minimum does not raise the count, because the backend never charges it.
  const effectiveParticipants = showWithMaxPrice
    ? chargedParticipantsFor(showWithMaxPrice)
    : bookingCart.groupSize;

  // Create BookingData object for pricing engine
  const bookingData: BookingData = {
    customer_type_id: customerTypeId,
    price_type_id: priceTypeId,
    participant_count: effectiveParticipants, // Use effective participants for pricing
    booking_add_ons: bookingAddOns,
  };

  // Always recalculate pricing based on current cart state
  // This ensures that changes to participant count, prices, or add-ons are reflected immediately
  const pricingTotals = calculateBookingTotals(
    bookingData,
    showUnitPriceExclVat,
    showVatPercentage,
  );

  const totalItems = bookingCart.shows.length + bookingCart.addOns.length;

  // Road-show shows total overrides the generic pricing engine: it's the base
  // fee + per-person rate * participants (excl VAT), with 25% VAT added on the
  // invoice. The footer summary uses these so it matches the road-show inputs.
  const isRoadShowCart = bookingCart.bookingType === "roadshow";
  const roadShowShowsBaseAmount =
    (effectiveRoadShowBase ?? 0) +
    (effectiveRoadShowPerPerson ?? 0) * bookingCart.groupSize;
  const roadShowShowsVatAmount = roadShowShowsBaseAmount * 0.25;
  const showsBaseAmount = isRoadShowCart
    ? roadShowShowsBaseAmount
    : pricingTotals.shows.baseAmount;
  const showsTotal = isRoadShowCart
    ? roadShowShowsBaseAmount + roadShowShowsVatAmount
    : pricingTotals.shows.total;
  const showsVatAmount = isRoadShowCart
    ? roadShowShowsVatAmount
    : pricingTotals.shows.vatAmount;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[50vw] sm:max-w-[50vw] p-0"
      >
        {/* Header */}
        <SheetHeader className="border-b px-6 py-4 bg-background">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingCart className="h-6 w-6" />
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </div>
              <SheetTitle className="text-xl">Booking cart</SheetTitle>
            </div>
            <div className="mr-8 flex items-center gap-2">
              <span className="text-red-500 text-lg font-bold">*</span>
              <Select
                value={bookingStatus ? bookingStatus.toString() : ""}
                onValueChange={(value) => {
                  setBookingStatus(parseInt(value));
                }}
              >
                <SelectTrigger className="w-[140px] h-9 text-sm font-medium border-2 bg-background hover:bg-accent">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={STATUS_IDS.BOOKING_DRAFT.toString()}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      <span>Draft</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={STATUS_IDS.BOOKING_PRELIMINARY.toString()}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span>Preliminary</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={STATUS_IDS.BOOKING_CONFIRMED.toString()}>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span>Reserved</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex flex-col h-[calc(100vh-80px)] overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-4 space-y-6">
              {/* Booking Details Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Booking Details
                </h3>

                <div className="space-y-4">
                  {/* First row - Participants and Price Mode */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="group-size">Participants</Label>
                      <Input
                        id="group-size"
                        type="number"
                        min="1"
                        max="50"
                        value={bookingCart.groupSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value) || 1;
                          if (onGroupSizeChange) {
                            onGroupSizeChange(newSize);
                          }
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Price mode</Label>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        <Button
                          size="sm"
                          variant={
                            priceMode ===
                            getPriceMode(PRICE_TYPE_IDS.PER_PERSON)
                              ? "default"
                              : "outline"
                          }
                          onClick={() =>
                            handlePriceModeChange(
                              getPriceMode(PRICE_TYPE_IDS.PER_PERSON),
                            )
                          }
                          disabled={bookingCart.bookingType === "roadshow"}
                          className="h-9 text-xs"
                        >
                          {PRICE_TYPE_NAMES[PRICE_TYPE_IDS.PER_PERSON]}
                        </Button>
                        <Button
                          size="sm"
                          variant={
                            priceMode === getPriceMode(PRICE_TYPE_IDS.FIXED)
                              ? "default"
                              : "outline"
                          }
                          onClick={() =>
                            handlePriceModeChange(
                              getPriceMode(PRICE_TYPE_IDS.FIXED),
                            )
                          }
                          disabled={bookingCart.bookingType === "roadshow"}
                          className="h-9 text-xs"
                        >
                          {PRICE_TYPE_NAMES[PRICE_TYPE_IDS.FIXED]}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Second row - Price and calculated total (non road-show) */}
                  {bookingCart.bookingType !== "roadshow" && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="booking-price">
                            {priceMode ===
                            getPriceMode(PRICE_TYPE_IDS.PER_PERSON)
                              ? `${PRICE_TYPE_NAMES[PRICE_TYPE_IDS.PER_PERSON]} Price`
                              : `${PRICE_TYPE_NAMES[PRICE_TYPE_IDS.FIXED]} Price`}{" "}
                            ({getCurrencySymbol(venueCurrency)}){" "}
                            {isPrivateCustomer ? "(incl. VAT)" : "(excl. VAT)"}
                          </Label>
                          <Input
                            id="booking-price"
                            type="number"
                            min="0"
                            value={
                              bookingCart.shows.length === 0 ? 0 : bookingPrice
                            }
                            onChange={(e) => {
                              onBookingPriceChange(
                                parseInt(e.target.value) || 0,
                              );
                              // Mark as manually overridden when user types
                              if (onPriceManuallyChanged) {
                                onPriceManuallyChanged(true);
                              }
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Total shows price</Label>
                          <div className="mt-1 h-9 px-3 py-2 border rounded-md bg-muted text-sm font-medium">
                            {formatPrice(
                              bookingCart.shows.length === 0
                                ? 0
                                : isPrivateCustomer
                                  ? pricingTotals.shows.total
                                  : pricingTotals.shows.baseAmount,
                            )}{" "}
                            {getCurrencySymbol(venueCurrency)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Road-show pricing: base fee + per-person rate (excl VAT).
                      Saved to bookings.show_base_price and
                      per_person_mode_price_excluding_vat. */}
                  {bookingCart.bookingType === "roadshow" && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="rs-base-price">
                            Base price ({getCurrencySymbol(venueCurrency)}) (excl.
                            VAT)
                          </Label>
                          <Input
                            id="rs-base-price"
                            type="number"
                            min="0"
                            value={effectiveRoadShowBase ?? ""}
                            onChange={(e) =>
                              onRoadShowBasePriceChange?.(
                                e.target.value === ""
                                  ? null
                                  : parseFloat(e.target.value),
                              )
                            }
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="rs-per-person">
                            Per person ({getCurrencySymbol(venueCurrency)}) (excl.
                            VAT)
                          </Label>
                          <Input
                            id="rs-per-person"
                            type="number"
                            min="0"
                            value={effectiveRoadShowPerPerson ?? ""}
                            onChange={(e) =>
                              onRoadShowPerPersonPriceChange?.(
                                e.target.value === ""
                                  ? null
                                  : parseFloat(e.target.value),
                              )
                            }
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Total shows price</Label>
                        <div className="mt-1 h-9 px-3 py-2 border rounded-md bg-muted text-sm font-medium">
                          {(() => {
                            const base = effectiveRoadShowBase ?? 0;
                            const pp = effectiveRoadShowPerPerson ?? 0;
                            const subtotal = base + pp * bookingCart.groupSize;
                            return formatPrice(subtotal);
                          })()}{" "}
                          {getCurrencySymbol(venueCurrency)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Third row - Date and show language */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="booking-date">Date</Label>
                      <Input
                        id="booking-date"
                        type="date"
                        value={(() => {
                          // Format date as YYYY-MM-DD without timezone issues
                          const year = effectiveBookingDate.getFullYear();
                          const month = String(
                            effectiveBookingDate.getMonth() + 1,
                          ).padStart(2, "0");
                          const day = String(
                            effectiveBookingDate.getDate(),
                          ).padStart(2, "0");
                          return `${year}-${month}-${day}`;
                        })()}
                        onChange={async (e) => {
                          // Parse the date value as local date to avoid timezone issues
                          const [year, month, day] = e.target.value
                            .split("-")
                            .map(Number);
                          const newDate = new Date(year, month - 1, day);
                          setBookingDate(newDate);

                          // Check for booking conflicts on the new date
                          await checkDateConflict(newDate);

                          // Notify parent component about date change
                          // This will trigger re-evaluation of price modifiers
                          if (onDateChange) {
                            onDateChange(newDate);
                          }
                        }}
                        className="mt-1"
                      />
                      {/* Show date conflict warnings */}
                      {dateConflicts.length > 0 && (
                        <div className="mt-2 p-2 rounded-md border">
                          {dateConflicts.map((message, index) => {
                            const isHardConflict = message.startsWith("🚫");
                            return (
                              <div
                                key={index}
                                className={`text-xs mb-1 ${
                                  isHardConflict ? "text-red-600" : "text-amber-600"
                                }`}
                              >
                                {message}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Language. Sets bookings.lang, which the email system uses
                        to pick the customer's confirmation-email language.
                        No preset — staff must actively choose. Lists only the
                        venue's available languages. */}
                    <div>
                      <Label htmlFor="booking-language">
                        Language <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={bookingCart.lang || undefined}
                        onValueChange={(value) => onLanguageChange?.(value)}
                      >
                        <SelectTrigger id="booking-language" className="mt-1">
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent>
                          {languageChoices.map((language) => (
                            <SelectItem
                              key={language.code}
                              value={language.code}
                            >
                              {language.flag} {language.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Road-show equipment kit. Lists only the kits available on
                        the selected day. Persisted as studio_show_host_bookings.kit_id. */}
                    {bookingCart.bookingType === "roadshow" && (
                      <div>
                        <Label htmlFor="booking-kit">
                          Kit <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={
                            bookingCart.kitId != null
                              ? bookingCart.kitId.toString()
                              : ""
                          }
                          onValueChange={(value) =>
                            onKitChange?.(value ? parseInt(value) : null)
                          }
                        >
                          <SelectTrigger id="booking-kit" className="mt-1">
                            <SelectValue placeholder="Select kit" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableKits.map((kit) => (
                              <SelectItem key={kit.id} value={kit.id.toString()}>
                                {kit.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {availableKits.length === 0 && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            No kits available on this day
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Cart Items Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Selected shows ({bookingCart.shows.length})
                </h3>

                {bookingCart.shows.length === 0 ? (
                  <div className="border rounded-lg p-6 text-center">
                    <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No shows selected</p>
                    <p className="text-sm text-muted-foreground">
                      Browse available shows to get started
                    </p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full min-w-[650px]">
                      <thead>
                        <tr className="border-b text-xs text-muted-foreground">
                          <th className="text-left p-2">Show</th>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Studio</th>
                          <th className="text-left px-1 py-2 min-w-[115px]">
                            Start Time
                          </th>
                          <th className="text-left px-1 py-2 min-w-[115px]">
                            End Time
                          </th>
                          <th className="text-left p-2">Capacity</th>
                          <th className="w-10 p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookingCart.shows.map((selectedShow, index) => {
                          const showName =
                            selectedShow.show.show_infos?.find(
                              (info) => info.lang === "en",
                            )?.name ||
                            selectedShow.show.show_infos?.[0]?.name ||
                            `Show ${selectedShow.show.id}`;
                          const duration =
                            selectedShow.show.duration_minutes || 60;
                          const [hours, minutes] = selectedShow.time
                            .split(":")
                            .map(Number);
                          const startDate = new Date();
                          startDate.setHours(hours, minutes, 0, 0);
                          const endDate = new Date(
                            startDate.getTime() + duration * 60000,
                          );
                          const calculatedEndTime = endDate
                            .toTimeString()
                            .slice(0, 5);

                          const handleTimeChange = (
                            type: "start" | "end",
                            value: string,
                          ) => {
                            if (onUpdateShowTime) {
                              if (type === "start") {
                                // Calculate new end time based on original duration
                                const [hour, min] = value.split(":").map(Number);
                                const startMinutes = hour * 60 + min;
                                const duration = selectedShow.show.duration_minutes || 60;
                                const endMinutes = startMinutes + duration;
                                const endHour = Math.floor(endMinutes / 60);
                                const endMin = endMinutes % 60;
                                const newEndTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;

                                onUpdateShowTime(
                                  index,
                                  value,
                                  newEndTime,
                                );
                                // Check for conflicts after updating
                                checkTimeConflict(index, value, newEndTime);
                              } else {
                                // End time changed - check if it matches the default
                                const [startHour, startMin] = selectedShow.time.split(":").map(Number);
                                const defaultDuration = selectedShow.show.duration_minutes || 60;
                                const defaultEndMinutes = (startHour * 60 + startMin) + defaultDuration;
                                const defaultEndHour = Math.floor(defaultEndMinutes / 60);
                                const defaultEndMin = defaultEndMinutes % 60;
                                const defaultEndTime = `${String(defaultEndHour).padStart(2, "0")}:${String(defaultEndMin).padStart(2, "0")}`;

                                // If the new end time matches the default, clear customEndTime
                                const endTimeToSet = value === defaultEndTime ? "" : value;

                                onUpdateShowTime(
                                  index,
                                  selectedShow.time,
                                  endTimeToSet,
                                );
                                // Check for conflicts with the updated duration
                                checkTimeConflict(
                                  index,
                                  selectedShow.time,
                                  endTimeToSet,
                                );
                              }
                            }
                          };

                          return (
                            <tr
                              key={`${selectedShow.show.id}-${selectedShow.date}-${index}`}
                              className="border-b"
                            >
                              <td className="p-2 text-sm">{showName}</td>
                              <td className="p-2 text-sm text-muted-foreground">
                                {(() => {
                                  // Parse date string as local date to avoid timezone issues
                                  const [year, month, day] = selectedShow.date
                                    .split("-")
                                    .map(Number);
                                  const localDate = new Date(
                                    year,
                                    month - 1,
                                    day,
                                  );
                                  return localDate.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  });
                                })()}
                              </td>
                              <td className="p-2">
                                {studios && onUpdateShowStudio ? (
                                  <Select
                                    value={selectedShow.studio?.id?.toString()}
                                    onValueChange={(value) => onUpdateShowStudio(index, parseInt(value))}
                                  >
                                    <SelectTrigger className="h-8 w-32 text-sm">
                                      <SelectValue placeholder="Select studio" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(() => {
                                        // Filter studios to only show those assigned to this show
                                        // The show_studios data is directly on the show object
                                        const showStudios = selectedShow.show?.show_studios || [];
                                        const assignedStudioIds = showStudios.map(ss => ss.studio_id);

                                        // Get unique studios - prevent duplicates
                                        const uniqueStudios = studios.filter((studio, index, self) =>
                                          index === self.findIndex(s => s.id === studio.id)
                                        );

                                        // Filter to only assigned studios if available
                                        const availableStudios = assignedStudioIds.length > 0
                                          ? uniqueStudios.filter(studio => assignedStudioIds.includes(studio.id))
                                          : uniqueStudios;

                                        return availableStudios.map((studio) => (
                                          <SelectItem
                                            key={studio.id}
                                            value={studio.id.toString()}
                                          >
                                            {studio.name} ({studio.capacity})
                                          </SelectItem>
                                        ));
                                      })()}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="text-sm text-muted-foreground">
                                    {selectedShow.studio?.name || "Studio A"}
                                  </span>
                                )}
                              </td>
                              <td className="px-1 py-2">
                                <Input
                                  type="time"
                                  value={selectedShow.time}
                                  onChange={(e) =>
                                    handleTimeChange("start", e.target.value)
                                  }
                                  className="h-8 w-28 text-sm px-2"
                                />
                              </td>
                              <td className="px-1 py-2">
                                <Input
                                  type="time"
                                  value={selectedShow.customEndTime || calculatedEndTime}
                                  onChange={(e) =>
                                    handleTimeChange("end", e.target.value)
                                  }
                                  className="h-8 w-28 text-sm px-2"
                                />
                              </td>
                              <td className="p-2 text-sm text-muted-foreground">
                                {selectedShow.show.min_participants}-
                                {selectedShow.show.max_participants}
                              </td>
                              <td className="p-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => onRemoveShow(index)}
                                  className="h-6 w-6 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {/* Show time conflict warnings */}
                    {Object.keys(timeConflicts).length > 0 && (
                      <div className="border-t">
                        {Object.entries(timeConflicts).map(([index, message]) => {
                          const isHardConflict = message.startsWith("🚫");
                          return (
                            <div
                              key={index}
                              className={`px-3 py-2 ${
                                isHardConflict
                                  ? "bg-red-50 border-b border-red-200"
                                  : "bg-amber-50 border-b border-amber-200"
                              }`}
                            >
                              <div className={`text-xs ${
                                isHardConflict ? "text-red-700" : "text-amber-700"
                              }`}>
                                {message}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="p-2 bg-muted/50 text-xs text-muted-foreground">
                      Booking base price:{" "}
                      {priceMode === "fixed"
                        ? `${formatPrice(bookingPrice)} ${getCurrencySymbol(venueCurrency)} fixed`
                        : `${formatPrice(bookingPrice)} ${getCurrencySymbol(venueCurrency)} × ${effectiveParticipants} = ${formatPrice(isPrivateCustomer ? pricingTotals.shows.total : pricingTotals.shows.baseAmount)} ${getCurrencySymbol(venueCurrency)}`}
                      {priceMode === "per_person" &&
                        isPrivateCustomer &&
                        " (incl. VAT)"}
                      {priceMode === "per_person" &&
                        !isPrivateCustomer &&
                        " (excl. VAT)"}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Selected Add-ons Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Selected add-ons ({bookingCart.addOns.length})
                </h3>

                {bookingCart.addOns.length === 0 ? (
                  <div className="border rounded-lg p-6 text-center">
                    <button
                      onClick={() => {
                        // Close the cart
                        onOpenChange(false);
                        // Trigger navigation to add-ons section
                        if (typeof window !== 'undefined') {
                          const event = new CustomEvent('navigateToAddons');
                          window.dispatchEvent(event);
                        }
                      }}
                      className="group inline-flex flex-col items-center hover:opacity-80 transition-opacity"
                    >
                      <Plus className="h-12 w-12 mx-auto text-muted-foreground mb-3 group-hover:text-primary transition-colors cursor-pointer" />
                      <p className="text-muted-foreground">No add-ons selected</p>
                      <p className="text-sm text-muted-foreground">
                        Click to add extras
                      </p>
                    </button>
                  </div>
                ) : (
                  <div className="border rounded-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-xs text-muted-foreground">
                          <th className="text-left p-2">Add-on</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2 w-20">Price</th>
                          <th className="text-left p-2 w-20">Qty</th>
                          <th className="text-left p-2 w-20">Subtotal</th>
                          {isPrivateCustomer && (
                            <th className="text-left p-2 w-20">VAT</th>
                          )}
                          <th className="text-left p-2 w-20">Total</th>
                          <th className="w-10 p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookingCart.addOns.map((item, index) => {
                          const price =
                            addonPrices[item.addOn.id] ?? item.addOn.price;
                          // Use actual quantity from cart item, not groupSize
                          // This allows per-person add-ons to have custom quantities
                          const quantity = item.quantity;

                          // Get values from booking add-on data
                          const addonData = bookingAddOns[index];
                          const subtotal = addonData.total_price_excl_vat || 0;
                          const addonVat = addonData.vat_amount || 0;
                          const addonTotal = subtotal + addonVat;

                          return (
                            <tr key={item.addOn.id} className="border-b">
                              <td className="p-2 text-sm">{item.addOn.name}</td>
                              <td className="p-2 text-sm text-muted-foreground">
                                {item.addOn.is_per_person
                                  ? "Per person"
                                  : "Individual"}
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="0"
                                  value={price}
                                  onChange={(e) =>
                                    handleAddonPriceChange(
                                      item.addOn.id,
                                      parseInt(e.target.value) || 0,
                                    )
                                  }
                                  className="h-7 w-16 text-xs"
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="number"
                                  min="1"
                                  value={quantity}
                                  onChange={(e) =>
                                    onQuantityChange(
                                      item.addOn.id,
                                      parseInt(e.target.value) || 1,
                                    )
                                  }
                                  className="h-7 w-16 text-xs"
                                />
                              </td>
                              <td className="p-2 text-sm font-medium">
                                {formatPrice(subtotal)}
                              </td>
                              {isPrivateCustomer && (
                                <td className="p-2 text-sm text-muted-foreground">
                                  {formatPrice(addonVat)}
                                </td>
                              )}
                              <td className="p-2 text-sm font-medium">
                                {formatPrice(
                                  isPrivateCustomer ? addonTotal : subtotal,
                                )}
                              </td>
                              <td className="p-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => onRemoveAddon(item.addOn.id)}
                                  className="h-6 w-6 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <Separator />

              {/* Customer Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer information
                </h3>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="customer-name">Full name *</Label>
                    <Input
                      id="customer-name"
                      placeholder="Enter customer name"
                      value={customerInfo.name}
                      onChange={(e) =>
                        setCustomerInfo((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer-email">Email *</Label>
                      <Input
                        id="customer-email"
                        type="email"
                        placeholder="customer@email.com"
                        value={customerInfo.email}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCustomerInfo((prev) => ({
                            ...prev,
                            email: value,
                          }));

                          // Validate email in real-time
                          if (value) {
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (!emailRegex.test(value)) {
                              setEmailError(
                                "Please enter a valid email address",
                              );
                            } else {
                              setEmailError("");
                            }
                          } else {
                            setEmailError("");
                          }
                        }}
                        className={emailError ? "mt-1 border-red-500" : "mt-1"}
                        required
                      />
                      {emailError && (
                        <p className="text-sm text-red-500 mt-1">
                          {emailError}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="customer-phone">Phone</Label>
                      <Input
                        id="customer-phone"
                        type="tel"
                        placeholder="e.g., 45 12 34 56 78 (optional)"
                        value={displayPhone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        onBlur={handlePhoneBlur}
                        className={phoneError ? "mt-1 border-red-500" : "mt-1"}
                      />
                      {phoneError ? (
                        <p className="text-sm text-red-500 mt-1">
                          {phoneError}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">
                          International format will be applied automatically (+ will be added)
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="customer-notes">Special requests</Label>
                    <Textarea
                      id="customer-notes"
                      placeholder="Any special requirements or notes..."
                      className="min-h-[80px] mt-1"
                      value={customerInfo.notes}
                      onChange={(e) =>
                        setCustomerInfo((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Company Information - Show only for corporate bookings */}
              {bookingCart.bookingType === "corporate" && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Company Information
                    </h3>

                    <div className="space-y-4">
                      {partners.length > 0 && (
                        <div>
                          <Label htmlFor="partner-select">
                            Partner{" "}
                            <span className="text-xs font-normal text-muted-foreground">
                              (optional — auto-fills company info)
                            </span>
                          </Label>
                          <Select
                            value={
                              selectedPartnerId !== null
                                ? String(selectedPartnerId)
                                : "__none__"
                            }
                            onValueChange={handlePartnerChange}
                          >
                            <SelectTrigger id="partner-select" className="mt-1">
                              <SelectValue placeholder="No partner" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">
                                No partner
                              </SelectItem>
                              {partners.map((p) => (
                                <SelectItem key={p.id} value={String(p.id)}>
                                  {p.company_name} ({p.commission_percentage}%)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedPartnerId !== null && (
                            <p className="text-xs text-purple-700 mt-1">
                              Partner commission will be applied to show fees.
                            </p>
                          )}
                        </div>
                      )}

                      {selectedPartnerId !== null && (
                        <div>
                          <Label htmlFor="end-customer-name">
                            End customer / Project name{" "}
                            <span className="text-xs font-normal text-muted-foreground">
                              (optional — shown on invoice)
                            </span>
                          </Label>
                          <Input
                            id="end-customer-name"
                            placeholder="e.g. Spotify AB"
                            value={endCustomerName}
                            onChange={(e) =>
                              setEndCustomerName(e.target.value)
                            }
                            className="mt-1"
                          />
                        </div>
                      )}

                      <div>
                        <Label htmlFor="invoice-reference">
                          Reference{" "}
                          <span className="text-xs font-normal text-muted-foreground">
                            {selectedPartnerId !== null
                              ? "(from partner — shown on invoice)"
                              : "(optional — shown on invoice)"}
                          </span>
                        </Label>
                        <Input
                          id="invoice-reference"
                          placeholder="e.g. PO-12345 or contact name"
                          value={companyInfo.invoiceReference}
                          maxLength={INVOICE_REFERENCE_MAX_LENGTH}
                          onChange={(e) =>
                            setCompanyInfo((prev) => ({
                              ...prev,
                              invoiceReference: e.target.value,
                            }))
                          }
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="company-name">Company Name *</Label>
                        <Input
                          id="company-name"
                          placeholder="Enter company name"
                          value={companyInfo.companyName}
                          onChange={(e) =>
                            setCompanyInfo((prev) => ({
                              ...prev,
                              companyName: e.target.value,
                            }))
                          }
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="org-number">
                          {isDanishVenue ? "CVR number" : "Organization Number"}
                        </Label>
                        <Input
                          id="org-number"
                          placeholder={orgNumberExample(orgNumberCountry)}
                          value={companyInfo.orgNumber}
                          onChange={(e) =>
                            setCompanyInfo((prev) => ({
                              ...prev,
                              orgNumber: formatOrgNumber(
                                e.target.value,
                                orgNumberCountry,
                              ),
                            }))
                          }
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="company-address">Address</Label>
                        <Input
                          id="company-address"
                          placeholder="Street address, City, Postal code"
                          value={companyInfo.address}
                          onChange={(e) =>
                            setCompanyInfo((prev) => ({
                              ...prev,
                              address: e.target.value,
                            }))
                          }
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Fixed Footer with Totals */}
          <div className="border-t bg-background p-6 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Booking ({bookingCart.shows.length} shows):</span>
                <span>
                  {formatPrice(
                    bookingCart.shows.length === 0
                      ? 0
                      : isPrivateCustomer
                        ? showsTotal
                        : showsBaseAmount,
                  )}{" "}
                  {getCurrencySymbol(venueCurrency)}
                </span>
              </div>
              {/* Minimum Charge Note in Footer */}
              {effectiveParticipants > bookingCart.groupSize && priceMode === "per_person" && (
                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  <strong>Minimum charge applies:</strong> {effectiveParticipants} participants (actual: {bookingCart.groupSize})
                </div>
              )}
              {activePriceModifier && (
                <div className="flex justify-between text-sm px-2 py-1.5 bg-blue-50 border border-blue-200 rounded">
                  <span className="flex items-center gap-2 text-blue-700">
                    <span>
                      {activePriceModifier.modifier_type === "discount"
                        ? "🎉"
                        : "⚠️"}
                    </span>
                    {activePriceModifier.name} (
                    {Math.abs(activePriceModifier.modifier_value)}%{" "}
                    {activePriceModifier.modifier_type === "discount"
                      ? "discount"
                      : "surcharge"}
                    ) - Already included in price
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span>Add-ons total:</span>
                <span>
                  {formatPrice(
                    isPrivateCustomer
                      ? pricingTotals.addOns.total
                      : pricingTotals.addOns.baseAmount,
                  )}{" "}
                  {getCurrencySymbol(venueCurrency)}
                </span>
              </div>
              <Separator />
              {isPrivateCustomer ? (
                <>
                  <div className="flex justify-between text-sm font-medium">
                    <span>Subtotal (incl. VAT):</span>
                    <span>
                      {formatPrice(
                        bookingCart.shows.length === 0
                          ? pricingTotals.addOns.total
                          : showsTotal + pricingTotals.addOns.total,
                      )}{" "}
                      {getCurrencySymbol(venueCurrency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground italic">
                    <span>- of which VAT:</span>
                    <span>
                      {formatPrice(
                        bookingCart.shows.length === 0
                          ? pricingTotals.addOns.vatAmount
                          : showsVatAmount + pricingTotals.addOns.vatAmount,
                      )}{" "}
                      {getCurrencySymbol(venueCurrency)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm font-medium">
                    <span>Subtotal (excl. VAT):</span>
                    <span>
                      {formatPrice(
                        bookingCart.shows.length === 0
                          ? pricingTotals.addOns.baseAmount
                          : showsBaseAmount + pricingTotals.addOns.baseAmount,
                      )}{" "}
                      {getCurrencySymbol(venueCurrency)}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground italic mt-1">
                    * VAT will be added on invoice
                  </div>
                </>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-semibold">
                <span>Total {pricingTotals.displayLabel}:</span>
                <span>
                  {formatPrice(
                    bookingCart.shows.length === 0
                      ? isPrivateCustomer
                        ? pricingTotals.addOns.total
                        : pricingTotals.addOns.baseAmount
                      : isPrivateCustomer
                        ? showsTotal + pricingTotals.addOns.total
                        : showsBaseAmount + pricingTotals.addOns.baseAmount,
                  )}{" "}
                  {getCurrencySymbol(venueCurrency)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              {bookingId ? (
                // When editing, show only the modify button
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => handleSaveBooking(false)}
                  disabled={
                    bookingCart.shows.length === 0 ||
                    !customerInfo.name.trim() ||
                    !customerInfo.email.trim() ||
                    !!phoneError ||
                    !!emailError ||
                    isSavingBooking ||
                    !bookingStatus
                  }
                >
                  {isSavingBooking
                    ? "Saving..."
                    : `Modify Booking (${totalItems} items)`}
                </Button>
              ) : (
                // When creating new booking, show only confirm button
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => handleSaveBooking(false)}
                  disabled={
                    bookingCart.shows.length === 0 ||
                    !customerInfo.name.trim() ||
                    !customerInfo.email.trim() ||
                    !!phoneError ||
                    !!emailError ||
                    isSavingBooking ||
                    !bookingStatus
                  }
                >
                  {isSavingBooking
                    ? "Saving..."
                    : `Create booking (${totalItems} items)`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
